/**
 * The V-plane scheduler's PURE half — the sheet effect catalog, the
 * drain order, the subsumption rules, and the source→effect routing
 * table, kept apart from a browser host's DOM wiring so a headless Deno
 * test can drive the full V1 emit → V2 resolve → V3 coalesce/subsume
 * pipeline with a stub executor and zero DOM. The host keeps only the
 * impure half:
 * `wireSheetInvalidation` binds effect ids to the live `$sheet`
 * rebind-handle methods.
 *
 * Pairs with `InvalidationTransaction` (the per-frame accumulator /
 * flush mechanism in this package): construct a
 * `InvalidationTransaction.Factory(subsumeSheetEffects, EFFECT_ORDER,
 * executor, schedule)` and feed it effects resolved by the sink built
 * here.
 */

import { InvalidationSinkImpl } from "@tento-core/invalidation";
import {
  CalendarPreferencesChanged,
  type CalendarPreferenceKind,
  ColumnDataChanged,
  Invalidation,
  SheetMetadataChanged,
  SheetWidthChanged,
  NewRowInserted,
  RowDataChanged,
  RowHeightChanged,
  RowLegendChanged,
  RowSyncStateChanged,
  SheetListChanged,
  TimepointsChanged,
} from "../../tx/invalidation.ts";
import type { RowLocalId } from "../../ids/row-local-id.ts";
import { GenericRange, MsSinceEpoch, type NaiveDate } from "@tento-chrono";
import type { Option } from "../../core/option.ts";
import { deepFrozen, frozenSet } from "../../core/frozen-collections.ts";
import { collectRowContentRanges, type RowContentRanges } from "./row-content-scope.ts";

/**
 * Geometry half of calendar-preference routing (ROUTING LAW —
 * `CalendarPreferencesChanged` in @tento/lona/sheets invalidation.ts
 * carries the kind in its path, `user.preferences.calendar.<kind>`,
 * and classifies nothing): the preferences whose change alters
 * rendered column/label geometry or sheet-wide cell formatting. Each
 * kind here is registered as a plain per-kind sink rule below
 * (`.when(pathFor(kind), CellContentAll)`) and gates a browser host's
 * whole-sheet clone reaction (at its bootstrap). Kinds NOT in this
 * set are row-scoped: the consuming alias templates declare them
 * (`AliasTemplate.reexpandOn`) and their repaint rides the declared
 * re-expansion → structural-event chain instead — they match no rule
 * here, so they resolve to no effects with no special-casing.
 * Exhaustiveness — every kind has at least one declared consumer — is
 * enforced by the routing suite in
 * tento-lona-js/tests/invalidation-pipeline.
 */
export const RENDER_GEOMETRY_PREFERENCES: ReadonlySet<CalendarPreferenceKind> =
  frozenSet<CalendarPreferenceKind>([
    "firstDay",
    "timeFormat",
    "dateFormat",
    "secondaryTimezones",
    "secondaryCalendars",
  ]);

/**
 * One effect a sheet element runs when an invalidation reaches it (measure
 * the legend, repaint a row, …).
 */
export class SheetEffect {
  /** An effect is process-global (one per kind, in `EFFECT_ORDER`), so it is frozen. */
  constructor(readonly id: string) {
    Object.freeze(this);
  }
}

/**
 * Values that go with `SheetEffect`: `CellHeights`, `StaticChildPos`,
 * `FixedChildPos`, `BodyMarginTop`, `LegendHeights`, `CellWidth`, and 12 more.
 */
export namespace SheetEffect {
  export const CellHeights = new SheetEffect("cell-heights");
  export const StaticChildPos = new SheetEffect("static-child-pos");
  export const FixedChildPos = new SheetEffect("fixed-child-pos");
  export const BodyMarginTop = new SheetEffect("body-margin-top");
  export const LegendHeights = new SheetEffect("legend-heights");
  export const CellWidth = new SheetEffect("cell-width");
  export const Decorations = new SheetEffect("decorations");
  export const CellContentStructure = new SheetEffect(
    "cell-content-structure",
  );
  export const CellContentAll = new SheetEffect("cell-content-all");
  export const CellContentRow = new SheetEffect("cell-content-row");
  export const CellContentColumn = new SheetEffect("cell-content-column");
  export const CellContentRange = new SheetEffect("cell-content-range");
  export const LegendContent = new SheetEffect("legend-content");
  export const LegendRow = new SheetEffect("legend-row");
  export const ViewportCallbacks = new SheetEffect("viewport-callbacks");
  export const DrawerContent = new SheetEffect("drawer-content");
  export const LegendMeasurement = new SheetEffect("legend-measurement");

  export const Layout: readonly SheetEffect[] = deepFrozen([
    LegendMeasurement,
    CellHeights,
    StaticChildPos,
    FixedChildPos,
    BodyMarginTop,
    LegendHeights,
    Decorations,
  ]);
}

/**
 * The order a sheet runs its effects in: layout first, then content, then
 * decorations.
 */
export const EFFECT_ORDER = [
  // Layout first (geometry must be correct before content binds)
  "legend-measurement",
  "cell-width",
  "body-margin-top",
  "cell-heights",
  "legend-heights",
  "static-child-pos",
  "fixed-child-pos",
  // Content second — EXISTENCE re-materializes before VALUES repaints.
  "cell-content-structure",
  "cell-content-all",
  "cell-content-row",
  "cell-content-column",
  "cell-content-range",
  // Legend third
  "legend-content",
  "legend-row",
  // Viewport callbacks
  "viewport-callbacks",
  // Drawer (secondary UI, after main sheet)
  "drawer-content",
  // Decorations last
  "decorations",
];
deepFrozen(EFFECT_ORDER);

/**
 * Union-only: the frame dirty set is the set-union of routed scopes; no
 * operation drops or widens a scope. The old cell-content-all/legend-content
 * erasure was the G2/G7 escalation trap — deleted by API absence. Physical
 * execution overlap (a whole-sheet rebind covering narrow scopes) is resolved
 * at the DRAIN via the frame's effect map, never here.
 *
 * Kept exported and named as the V3 normalization seam for L6 (tests + lib.ts
 * export it); the body is intentionally empty under the union-only law.
 */
export function subsumeSheetEffects(
  effects: Map<string, Invalidation[]>,
): void {
  void effects;
}

// ── Frame coverage plan (the last pure half) ──────────────────────

/**
 * Which effect owns a frame's single whole-sheet pass. `structure`
 * re-materializes before `cell-content-all` in {@link EFFECT_ORDER}, so
 * when both are dirty in one frame structure owns the pass and
 * `cell-content-all` yields to it.
 */
export type WholeSheetOwner = "cell-content-structure" | "cell-content-all";

const WHOLE_SHEET_STRUCTURE: WholeSheetOwner = "cell-content-structure";
const WHOLE_SHEET_ALL: WholeSheetOwner = "cell-content-all";

/**
 * The narrow content scopes a whole-sheet pass physically covers: each
 * rebinds a subset of what `rebindAll` / `rebindStructure` already
 * rebound this frame, so running them after it is duplicated work.
 */
const NARROW_CONTENT_EFFECTS: ReadonlySet<string> = new Set<string>([
  SheetEffect.CellContentRow.id,
  SheetEffect.CellContentColumn.id,
  SheetEffect.CellContentRange.id,
]);

/**
 * Input to {@link planFrame}: one frame's dirty set, plus the one fact
 * the SDK cannot derive on its own.
 */
export interface FramePlanInput {
  /**
   * The frame's effect map exactly as the drain hands it to an
   * `EffectExecutor` — keyed by effect **id** (`SheetEffect.id`), which
   * is what `InvalidationTransaction` accumulates and drains.
   */
  readonly frame: ReadonlyMap<string, readonly Invalidation[]>;
  /**
   * Whether these sources are a transparent in-place re-key rather than
   * real content movement — today, a copy-on-write PROMOTE
   * (`SheetsClient.PROMOTE_ACTOR`, matched by actor name in
   * a browser host's sheet-invalidation dispatch). INJECTED because the
   * accessor that mints that actor is not reachable through a barrel
   * this package imports; the host knows it, the plan does not need to.
   * A transparent re-key skips its own whole-sheet rebind, so it is NOT
   * a whole-sheet pass and must not suppress the narrow scopes.
   */
  readonly isTransparentRekey: (sources: readonly Invalidation[]) => boolean;
}

/**
 * The per-frame coverage plan: at most ONE whole-sheet pass runs per
 * frame, so the narrow scopes it physically covers can return early.
 * Pure and stateless — derived from this frame's effect map alone, with
 * no cross-frame memory (that would be subsumption, which the union-only
 * law of {@link subsumeSheetEffects} forbids; coverage is an execution
 * fact resolved at the drain, never a dropped scope).
 */
export interface FramePlan {
  /** Which effect runs this frame's whole-sheet pass, if any. */
  readonly wholeSheetOwner: Option<WholeSheetOwner>;
  /**
   * `true` ⇒ this effect's work is already covered by the frame's
   * whole-sheet pass and its dispatch arm may return early.
   *
   * Scope: the CELL-content plane only. The legend plane's own coverage
   * relation (`legend-content` unconditionally rebinds every legend, so
   * `legend-row` yields to it) is still decided app-side and is
   * deliberately not modelled here — `covered("legend-row")` is always
   * `false`. It moves the whole-sheet plan and nothing else.
   */
  covered(effectId: string): boolean;
}

/**
 * Compute a frame's coverage plan. Exactly the predicate table a host's
 * dispatch arms encoded inline before this plan:
 *
 * - `cell-content-all` yields its rebind to `cell-content-structure`
 *   when both are dirty (`!structural` guarded the rebind).
 * - `cell-content-row` / `-column` / `-range` return early when either
 *   whole-sheet pass will run (`structural || allWillRebind`).
 * - a promote-only `cell-content-all` is NOT a whole-sheet pass: it
 *   skips its own rebind to keep the focused editor's caret, so it owns
 *   nothing and suppresses nothing (the latent bug that rule fixed —
 *   the narrow scopes were wrongly suppressed by a pass that never ran).
 * - every other effect (layout, structure itself, the legend plane,
 *   viewport, drawer) is never covered.
 */
export function planFrame(input: FramePlanInput): FramePlan {
  const structural = input.frame.has(WHOLE_SHEET_STRUCTURE);
  const allSources = input.frame.get(WHOLE_SHEET_ALL);
  const allWillRebind =
    allSources != null && !input.isTransparentRekey(allSources);
  const wholeSheetOwner: Option<WholeSheetOwner> = structural
    ? WHOLE_SHEET_STRUCTURE
    : allWillRebind
    ? WHOLE_SHEET_ALL
    : null;

  return {
    wholeSheetOwner,
    covered(effectId: string): boolean {
      // The whole-sheet effects: `all` yields to `structure` (and only
      // to it); `structure` owns the pass and yields to nobody.
      if (effectId === WHOLE_SHEET_ALL) {
        return wholeSheetOwner === WHOLE_SHEET_STRUCTURE;
      }
      // The narrow scopes: covered by whichever pass owns the frame.
      return NARROW_CONTENT_EFFECTS.has(effectId) && wholeSheetOwner != null;
    },
  };
}

// ── Effect → dirty scope (the executors' one table) ────────────────

/**
 * Whether `sources` are ALL a copy-on-write PROMOTE (`SheetListChanged` from
 * the actor named `sheet-promote`, `SheetsClient.PROMOTE_ACTOR`): a
 * transparent in-place re-key. Its `cell-content-all` skips the whole-sheet
 * rebind (the focused editor keeps its caret) and is no whole-sheet pass
 * (`planFrame`'s `isTransparentRekey`).
 */
export function isPromoteOnly(sources: readonly Invalidation[]): boolean {
  return sources.length > 0 &&
    sources.every((s) => s instanceof SheetListChanged && s.actor.name === "sheet-promote");
}

/**
 * What one drained effect re-renders — the scope, never the DOM call. Both
 * executors read it: a browser host's `wireSheetInvalidation` dispatch (the
 * rebind-handle call per kind) and the TUI render loop (`cli/frame.ts`
 * `applyEffect`). Coverage by the frame's whole-sheet pass is `planFrame`'s,
 * decided before the scope is read.
 *
 * - `layout` — geometry only (the effect id names which); no content re-read.
 * - `structure` — the whole sheet re-materializes; `rowInsert` re-keys in
 *   place (`rebindStructure`) instead of rebinding everything.
 * - `all` — every cell re-reads, unless `transparentRekey` (a promote-only
 *   frame, `isPromoteOnly`); `theme` when a theme preference is a source
 *   (the sheet's background CSS variable).
 * - `rows` — per row, the date ranges to re-read (`null`: the whole row):
 *   every source naming a row but `RowHeightChanged`, a `RowDataChanged`'s
 *   range, whole-row otherwise (`collectRowContentRanges`).
 * - `columns` — each distinct column partial a `ColumnDataChanged` names,
 *   ONE `span`: the hull of the timed sources' `instants` (a datetime cell's,
 *   `ColumnDataChanged.timed`; `null` when none carried any), and `spans`:
 *   each timed source's own row and instants, once each, in arrival order
 *   (empty when none). The columns are UTC storage dates; a surface whose
 *   columns are the viewer's days also rebinds the columns the instants
 *   meet, as it does a `range`. A surface that re-reads by row narrows to
 *   `spans`: two timed edits on two rows months apart in one batch are two
 *   one-row spans, not a hull over every day between them for both rows.
 * - `range` — ONE span, the union of every `TimepointsChanged` range
 *   (`merged` sources folded; `null` when none carried a range).
 * - `legend-all`, `legend-rows` (the rows a `RowLegendChanged` names),
 *   `legend-measurement`, `viewport`, `drawer`, `decorations`, and
 *   `unknown` for an id the catalog does not hold.
 */
export type SheetEffectScope =
  | { readonly kind: "layout"; readonly effect: string }
  | { readonly kind: "structure"; readonly rowInsert: boolean }
  | { readonly kind: "all"; readonly transparentRekey: boolean; readonly theme: boolean }
  | { readonly kind: "rows"; readonly rows: ReadonlyMap<RowLocalId, RowContentRanges> }
  | {
    readonly kind: "columns";
    readonly columns: readonly NaiveDate.Partial[];
    readonly span: Option<GenericRange<MsSinceEpoch>>;
    readonly spans: readonly { readonly rowId: Option<RowLocalId>; readonly span: GenericRange<MsSinceEpoch> }[];
  }
  | { readonly kind: "range"; readonly span: Option<GenericRange<MsSinceEpoch>>; readonly merged: number }
  | { readonly kind: "legend-all" }
  | { readonly kind: "legend-rows"; readonly rows: readonly RowLocalId[] }
  | { readonly kind: "legend-measurement" }
  | { readonly kind: "viewport" }
  | { readonly kind: "drawer" }
  | { readonly kind: "decorations" }
  | { readonly kind: "unknown"; readonly effect: string };

/** The layout effect ids: geometry, no content. */
const LAYOUT_EFFECTS: ReadonlySet<string> = new Set<string>([
  "cell-heights",
  "static-child-pos",
  "fixed-child-pos",
  "body-margin-top",
  "cell-width",
  "legend-heights",
]);

/**
 * A source's row, for the invalidations whose path is `rows.<id>…` (what the
 * `rows.*` route hands `cell-content-row`): `null` for any other.
 */
function rowOf(source: Invalidation): Option<RowLocalId> {
  if (
    source instanceof RowDataChanged || source instanceof RowLegendChanged ||
    source instanceof RowSyncStateChanged || source instanceof RowHeightChanged
  ) {
    return source.rowId;
  }
  return null;
}

/**
 * The dirty scope effect `effect` names for its drained `sources` (see
 * `SheetEffectScope`). Pure: no DOM, no sheet, no frame.
 */
export function scopeOf(effect: string, sources: readonly Invalidation[]): SheetEffectScope {
  if (LAYOUT_EFFECTS.has(effect)) return { kind: "layout", effect };
  switch (effect) {
    case "cell-content-structure":
      return { kind: "structure", rowInsert: sources.some((s) => s instanceof NewRowInserted) };
    case "cell-content-all":
      return {
        kind: "all",
        transparentRekey: isPromoteOnly(sources),
        theme: sources.some((s) => Invalidation.matches(s, "user.preferences.theme")),
      };
    case "cell-content-row": {
      const rows = collectRowContentRanges(
        sources
          .filter((s) => !(s instanceof RowHeightChanged))
          .flatMap((s) => {
            const rowId = rowOf(s);
            return rowId == null ? [] : [{ rowId, range: s instanceof RowDataChanged ? s.range : null }];
          }),
      );
      return { kind: "rows", rows };
    }
    case "cell-content-column": {
      const seen = new Set<string>();
      const columns: NaiveDate.Partial[] = [];
      const seenSpans = new Set<string>();
      const spans: { readonly rowId: Option<RowLocalId>; readonly span: GenericRange<MsSinceEpoch> }[] = [];
      let start: Option<MsSinceEpoch> = null;
      let end: Option<MsSinceEpoch> = null;
      for (const s of sources) {
        if (!(s instanceof ColumnDataChanged)) continue;
        const instants = s.instants;
        if (instants != null) {
          if (start == null || instants.start < start) start = MsSinceEpoch.of(instants.start);
          if (end == null || instants.end > end) end = MsSinceEpoch.of(instants.end);
          const spanKey = `${s.rowId ?? "*"}|${instants.start}|${instants.end}`;
          if (!seenSpans.has(spanKey)) {
            seenSpans.add(spanKey);
            spans.push({ rowId: s.rowId, span: new GenericRange(MsSinceEpoch.of(instants.start), MsSinceEpoch.of(instants.end)) });
          }
        }
        const key = s.column.toString();
        if (seen.has(key)) continue;
        seen.add(key);
        columns.push(s.column);
      }
      return { kind: "columns", columns, span: start != null && end != null ? new GenericRange(start, end) : null, spans };
    }
    case "cell-content-range": {
      let start: Option<MsSinceEpoch> = null;
      let end: Option<MsSinceEpoch> = null;
      for (const s of sources) {
        if (!(s instanceof TimepointsChanged)) continue;
        if (start == null || s.range.start < start) start = s.range.start;
        if (end == null || s.range.end > end) end = s.range.end;
      }
      return { kind: "range", span: start != null && end != null ? new GenericRange(start, end) : null, merged: sources.length };
    }
    case "legend-content":
      return { kind: "legend-all" };
    case "legend-row": {
      const seen = new Set<RowLocalId>();
      for (const s of sources) if (s instanceof RowLegendChanged) seen.add(s.rowId);
      return { kind: "legend-rows", rows: [...seen] };
    }
    case "legend-measurement":
      return { kind: "legend-measurement" };
    case "viewport-callbacks":
      return { kind: "viewport" };
    case "drawer-content":
      return { kind: "drawer" };
    case "decorations":
      return { kind: "decorations" };
  }
  return { kind: "unknown", effect };
}

/**
 * The invalidation sink a sheet element publishes into, resolving each
 * invalidation to the sheet effects it needs. Made only by
 * `SheetInvalidationSink.wired()`, which installs the effect routes: a sink
 * without them would resolve every invalidation to nothing.
 */
export class SheetInvalidationSink extends InvalidationSinkImpl<SheetEffect> {
  private constructor() {
    super();
  }

  /**
   * A sink with every sheet effect route installed: the one a sheet element
   * listens on, mapping each invalidation to the effects it runs.
   */
  static wired(): SheetInvalidationSink {
    return wireSheetEffectRoutes(new SheetInvalidationSink());
  }

  override resolve(event: Invalidation): SheetEffect[] {
    const effects = super.resolve(event);

    // Core invalidation matching is intentionally prefix-based:
    // `rows.*` matches `rows.<id>.cells.<date>`. SDK 2.0 cell writes
    // are column-scoped, so keep scalar cell edits from also driving a
    // whole-row content rebind.
    if (event instanceof ColumnDataChanged) {
      // Filtered only when the row effect is there (a cell's resolve runs per installed cell).
      return effects.includes(SheetEffect.CellContentRow)
        ? effects.filter((effect) => effect !== SheetEffect.CellContentRow)
        : effects;
    }

    // The `sheets` rule is meant to route ONLY `SheetListChanged` (its path is
    // literally "sheets"). Prefix matching would otherwise leak the
    // whole-content `cell-content-all` onto EVERY deeper `sheets.<...>` path
    // (existence, values, lifecycle, metadata) — phantom whole-content dirt on
    // every structural frame. Make it exact by stripping `cell-content-all`
    // from deeper sheets paths.
    if (event.path.startsWith("sheets.")) {
      // Orphan preservation: `SheetMetadataChanged` (`sheets.<id>`, a
      // title/metadata change) and `SheetWidthChanged` (`sheets.<id>.width`)
      // have NO other rule — the catch-all was their only route and each must
      // still repaint. Their paths are parameterized by sheetId, so no clean
      // static `.when` pattern matches them without re-introducing the prefix
      // over-match; preserve their pre-change whole-content route here.
      if (
        event instanceof SheetMetadataChanged ||
        event instanceof SheetWidthChanged
      ) {
        return effects;
      }
      return effects.filter((effect) => effect !== SheetEffect.CellContentAll);
    }

    return effects;
  }
}

/** `sink` with the sheet effect routes installed (`SheetInvalidationSink.wired`). */
function wireSheetEffectRoutes(empty: SheetInvalidationSink): SheetInvalidationSink {
  const sink = empty
    // Layout sources
    .when("rows.*.heightPx", ...SheetEffect.Layout)
    .when(
      "ui.layout",
      ...SheetEffect.Layout,
      SheetEffect.ViewportCallbacks,
      SheetEffect.DrawerContent,
    )
    .when("ui.viewport", SheetEffect.CellWidth, SheetEffect.FixedChildPos)
    // Content sources
    .when(
      "user.preferences.timezone",
      SheetEffect.CellContentAll,
      SheetEffect.LegendContent,
    )
    .when(
      "user.preferences.theme",
      SheetEffect.CellContentAll,
      SheetEffect.Decorations,
    )
    .when("tasks", SheetEffect.CellContentAll)
    .when("calendars.visibility", SheetEffect.CellContentAll)
    .when(
      "sheets.*.rows",
      ...SheetEffect.Layout,
      SheetEffect.CellContentStructure,
      SheetEffect.LegendContent,
      SheetEffect.DrawerContent,
    )
    .when(
      "sheets.*.rows.reorder",
      ...SheetEffect.Layout,
      SheetEffect.CellContentStructure,
      SheetEffect.LegendContent,
      SheetEffect.DrawerContent,
    )
    // Scoped content
    .when("rows.*", SheetEffect.CellContentRow)
    .when(
      "rows.*.attributes",
      ...SheetEffect.Layout,
      SheetEffect.CellContentRow,
      SheetEffect.LegendContent,
      SheetEffect.DrawerContent,
    )
    .when("rows.*.cells", SheetEffect.CellContentColumn)
    .when("calendars.events.*", SheetEffect.CellContentRange)
    .when("rows.*.label", SheetEffect.LegendMeasurement)
    // Viewport sources
    .when("calendars.events", SheetEffect.ViewportCallbacks)
    // Structural changes
    .when(
      "sheets.*.structure",
      ...SheetEffect.Layout,
      SheetEffect.CellContentStructure,
      SheetEffect.LegendContent,
      SheetEffect.DrawerContent,
    )
    // Sheet list / metadata
    .when("sheets", SheetEffect.CellContentAll);

  // Calendar preferences route per-kind (the event path carries the
  // kind): only render-geometry kinds map to the whole-sheet rebind.
  // Row-scoped kinds (e.g. defaultWeatherLocation) intentionally have
  // NO rule — their repaint rides the template-declared re-expansion →
  // structural-event chain (alias-reexpand-rebind suite). A coarse
  // `user.preferences.calendar` rule here would resurrect the
  // unrelated-cell flash via parent subsumption. See
  // .requirements/structural-rebind —
  // relocation-does-not-flash-unrelated-cells, driver (3).
  for (const kind of RENDER_GEOMETRY_PREFERENCES) {
    sink.when(
      CalendarPreferencesChanged.pathFor(kind),
      SheetEffect.CellContentAll,
    );
  }

  return sink;
}
